import { useState } from "react";
import { api, apiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { toast } from "sonner";
import { Pencil } from "lucide-react";

export default function EditProjectDialog({ project, users, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);

  const init = () => setForm({
    name: project.name, description: project.description || "", status: project.status,
    start_date: project.start_date || "", end_date: project.end_date || "",
    member_ids: project.member_ids || [],
  });

  const toggleMember = (id) => setForm((f) => ({
    ...f,
    member_ids: f.member_ids.includes(id) ? f.member_ids.filter((m) => m !== id) : [...f.member_ids, id],
  }));

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/projects/${project.id}`, form);
      toast.success("Project updated");
      setOpen(false);
      reload();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) init(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="edit-project-btn">
          <Pencil className="h-4 w-4 mr-1.5" /> Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Edit Project</DialogTitle></DialogHeader>
        {form && (
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5"><Label>Name</Label>
              <Input data-testid="edit-project-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
            <div className="space-y-1.5"><Label>Description</Label>
              <Textarea data-testid="edit-project-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="edit-project-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["planning", "active", "on_hold", "archived"].map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Start date</Label>
                <Input type="date" data-testid="edit-project-start-input" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>End date</Label>
                <Input type="date" data-testid="edit-project-end-input" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
            </div>
            <div className="space-y-2">
              <Label>Team members</Label>
              <div className="border border-border rounded-md divide-y divide-border max-h-48 overflow-y-auto">
                {users.map((u) => (
                  <label key={u.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-accent/50 transition-colors"
                    data-testid={`edit-project-member-${u.email.split("@")[0]}`}>
                    <Checkbox checked={form.member_ids.includes(u.id)} onCheckedChange={() => toggleMember(u.id)} />
                    <span className="text-sm">{u.name}</span>
                    <span className="text-[10px] font-mono uppercase text-muted-foreground ml-auto">{u.role}</span>
                  </label>
                ))}
              </div>
            </div>
            <Button type="submit" data-testid="edit-project-save-btn" className="w-full">Save Changes</Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
